import { Request } from './lib/Request';
import { ThunkAction } from 'redux-thunk';
import { Action } from 'redux';
import { TSFix } from '@spotify-internal/ts-migration-utils/types';

export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export interface FeatureMap {
  [feature: string]: FeatureValue;
}

export type FeatureValue = 'Release' | 'Control';

export interface Me {
  employee: boolean;
  uri: string;
  permissions: {
    godMode: boolean;
    network: boolean;
    podcast: boolean;
  };
  displayName?: string;
  avatarUrl?: string;
  emailAddress?: string;
  features: FeatureMap;
  dataRange: DataRange;
  needsToAcceptTerms: boolean;
}

export enum UserType {
  employee = 'employee',
  network = 'network',
  podcast = 'podcast',
  empty = 'empty',
}

export type DataRange = {
  from: string;
  until: string;
};

interface iTunesSubcategory {
  name: string;
}
interface iTunesCategory {
  name: string;
  subcategories: iTunesSubcategory[];
}

export interface Show {
  artworkUrl: string;
  claimStatus: string;
  consumptionOrder: string;
  coverImage: string;
  description: string;
  explict: boolean;
  id: string;
  language: string;
  name: string;
  published: boolean;
  publisher: string;
  redactedEmailAddress: string;
  releaseDate: string;
  totalEpisodes: number;
  url: string;
  starts: number;
  streams: number;
  listeners: number;
  followers: number;
  trailer?: Trailer | null;
  network: Network;
  feedUrl: string;
  taxi?: boolean;
}

export type PendingPodcast = {
  artworkUrl: string;
  name: string;
  author: string;
  status: string;
};

export type TopPodcast = {
  artworkUrl: string;
  consumptionOrder: string;
  coverImage: string;
  description: string;
  explict: boolean;
  id: string;
  language: string;
  name: string;
  published: boolean;
  publisher: string;
  releaseDate: string;
  sparkLine: SparkLine;
  starts: number;
  totalEpisodes: number;
  url: string;
};

export type MeShows = {
  artworkUrl: string;
  consumptionOrder: string;
  coverImage: string;
  description: string;
  explict: boolean;
  id: string;
  language: string;
  name: string;
  published: boolean;
  publisher: string;
  releaseDate: string;
  starts: number | null;
  streams: number | null;
  listeners: number | null;
  totalEpisodes: number;
  url: string;
};

export type SparkLine = { date: string; streams: number }[];

export interface Podcast {
  feedUrl: string;
  author: string;
  description: string;
  email: string;
  errors: string[];
  hasVideo: boolean;
  image: string;
  itunesCategories: iTunesCategory[];
  language: string;
  network: { id: string; name: string };
  shows: Show[];
  title: string;
  isExistingShow: boolean;
}

export interface Network {
  id: string;
  name: string;
  doingBusinessAs?: string;
  passthroughCapable: boolean;
}

export type NetworkMetadata = {
  followers: number;
  id: string;
  listeners: number;
  name: string;
  passthroughCapable: boolean;
  starts: number;
  streams: number;
};

export interface Category {
  name: string;
  value: string;
  subcategories: Subcategory[];
}

export interface SelectedCategory extends Category {
  categoryKey: string;
}

export interface Subcategory {
  name: string;
  value: string;
}

export interface Country {
  name: string;
  code: string;
}

export interface Language {
  name: string;
  code: string;
}

export interface PodcastMetadata {
  countryOfOrigin: Country;
  language: Language;
  categories: Category[];
}

export interface TopArtist {
  type: 'ARTIST';
  title: string;
  uri: string;
  image: string;
}

export type ShareAction = 'copy-event' | 'copy-button' | 'download-badges';

export type ReduxState = {
  data: ReduxData;
  status: ReduxStatus;
};

export type ReduxData = {
  me?: Me;
  meShows?: {
    metadata: {
      network: Pick<Network, 'id' | 'name'>;
      startDate: string;
      endDate: string;
    };
    shows: MeShows[];
  } & PaginatedShows;
  rssFeed?: Podcast;
  submitFeed?: string;
  verifiedContract?: boolean;
  aggregatorsState?: Network;
  pendingPodcasts?: PendingPodcast[];
  countries?: Country[];
  categoriesState?: SelectedCategory[];
  currentNetworkId?: string;
  dateRange?: DateRangeWithLabel;
  episodeMetadata?: EpisodeMetadata;
  episodePerformance?: EpisodePerformance;
  episodeV2Enabled?: boolean;
  episodeStartsStreams?: DetailedStream[];
  episodeList?: PaginatedEpisodes;
  episodeDemographics?: EpisodeAudienceDemographics;
  languages?: Language[];
  networkFollowers?: Follower[];
  networkList?: NetworkListResult[];
  networkSearchResults?: {
    results: NetworkListResult[];
    filter: string;
  };
  networkListeners?: Listener[];
  networkStartsStreams?: DetailedStream[];
  networkDemographics?: NetworkAudienceDemographics;
  podcastStartsStreams?: DetailedStream[];
  podcastListeners?: Listener[];
  podcastFollowers?: Follower[];
  podcastDemographics?: PodcastAudienceDemographics;
  networkMetadata?: NetworkMetadata;
  networkTopPodcasts?: TopPodcast[];
  networkTopEpisodes?: TopEpisode[];
  [key: string]: undefined | TSFix;
};

export type ReduxStatus = {
  me?: 'LOADING' | 'ERROR' | 'UNAUTHORIZED';
  rssFeed?: 'LOADING' | 'ERROR' | 'UNAUTHORIZED';
  submitFeed?: 'LOADING' | 'ERROR' | 'SUBMIT_SUCCESS' | 'UNAUTHORIZED' | 'CONFLICT';
  countries?: 'LOADING' | 'ERROR';
  languages?: 'LOADING' | 'ERROR';
  meShows?: 'LOADING' | 'ERROR';
  pendingPodcasts?: 'LOADING' | 'ERROR';
  episodeMetadata?: 'LOADING' | 'UNAUTHORIZED' | 'ERROR';
  episodeList?: 'LOADING' | 'ERROR';
  episodeDemographics?: 'LOADING' | 'ERROR';
  episodeListeners?: 'LOADING' | 'ERROR';
  episodePerformance?: 'LOADING' | 'ERROR';
  episodeStartsStreams?: 'LOADING' | 'ERROR';
  networkList?: 'LOADING' | 'ERROR';
  networkStartsStreams?: 'LOADING' | 'ERROR';
  checkVerificationCode?: 'LOADING' | 'SUCCESS' | 'ERROR';
  networkListeners?: 'LOADING' | 'ERROR';
  networkFollowers?: 'LOADING' | 'ERROR';
  networkSearchResults?: 'LOADING' | 'ERROR';
  podcastFollowers?: 'LOADING' | 'ERROR';
  podcastDemographics?: 'LOADING' | 'ERROR';
  networkDemographics?: 'LOADING' | 'ERROR';
  podcastStartsStreams?: 'LOADING' | 'ERROR';
  podcastListeners?: 'LOADING' | 'ERROR';
  networkMetadata?: 'LOADING' | 'ERROR' | 'UNAUTHORIZED';
  networkTopPodcasts?: 'LOADING' | 'ERROR';
  networkTopEpisodes?: 'LOADING' | 'ERROR';
  [key: string]: undefined | TSFix;
};

export type ReduxContext = {
  request: Request;
  pkgVersion: string;
  version: string;
};

export type Thunk = ThunkAction<any, ReduxState, ReduxContext, Action<string>>;

export type FAQ = {
  question: string;
  answer: React.ReactNode;
};

export type DateRangeWithLabel = {
  startDate: string;
  endDate: string;
  label: string;
};

export type EpisodeMetadata = {
  artworkUrl: string;
  description: string;
  duration: number;
  explict: boolean;
  id: number;
  language: string;
  listeners: number;
  name: string;
  network: { id: string; name: string };
  releaseDate: string;
  show: { id: string; name: string };
  starts: number;
  streams: number;
  url: string;
};

export type TopEpisode = {
  artworkUrl: string;
  description: string;
  duration: number;
  explict: boolean;
  id: string;
  language: string;
  name: string;
  releaseDate: string;
  sparkLine: SparkLine;
  starts: number;
  url: string;
};

export type EpisodePerformance = {
  max: number;
  medianCompletion: { percentage: number; seconds: number };
  percentiles: {
    25: number;
    50: number;
    75: number;
    100: number;
  };
  samples: number[];
  seconds: number;
  performanceData: {
    timestamp: string;
    retention: number;
  }[];
};

export type DetailedStream = {
  date: string;
  starts: number;
  streams: number;
};

export type Listener = {
  count: number;
  date: string;
};

export type Follower = {
  count: number;
  date: string;
};

export type PodcastAudienceDemographics = {
  count: number;
  start: string;
  end: string;
  ageFacetedCounts: AgeFacetedCounts;
  countryFacetedCounts: CountryFactedCounts;
  genderedCounts: GenderedCounts;
  genderDemographicsByAgeGroup: GenderDemographics[];
  percentageByGender: GenderWithPercentage[];
  countryDemographics: CountryDemographics[];
};

export type NetworkListResult = Pick<Network, 'id' | 'name'>;

export type NetworkAudienceDemographics = {
  count: number;
  start: string;
  end: string;
  ageFacetedCounts: AgeFacetedCounts;
  countryFacetedCounts: CountryFactedCounts;
  genderedCounts: GenderedCounts;
  genderDemographicsByAgeGroup: GenderDemographics[];
  percentageByGender: GenderWithPercentage[];
  countryDemographics: CountryDemographics[];
};

export type EpisodeAudienceDemographics = {
  count: number;
  start: string;
  end: string;
  ageFacetedCounts: AgeFacetedCounts;
  countryFacetedCounts: CountryFactedCounts;
  genderedCounts: GenderedCounts;
  genderDemographicsByAgeGroup: GenderDemographics[];
  percentageByGender: GenderWithPercentage[];
  countryDemographics: CountryDemographics[];
  demographicsMetadata: {
    gender: string;
    percentage: number;
  } & CountryDemographics;
};

export type AgeFacetedCategories =
  | '0-17'
  | '18-22'
  | '23-27'
  | '28-34'
  | '35-44'
  | '45-59'
  | '60-150'
  | 'unknown';

export type GenderedCounts = {
  counts: {
    NOT_SPECIFIED: number;
    NON_BINARY: number;
    MALE: number;
    FEMALE: number;
  };
};

export type AgeFacetedCounts = {
  [key in AgeFacetedCategories]: GenderedCounts;
};

export type CountryFactedCounts = {
  [country: string]: GenderedCounts & { countryCode: string };
};

export type CountryDemographics = { name: string; code: string; count: number };

export type GenderDemographics = {
  ageRange: string;
  ageRangePercentage: number;
} & GenderDemographicsPercentagePerGender &
  GenderDemographicsPercentagePerRange;

export type GenderDemographicsPercentagePerRange = {
  notSpecifiedPercentage: number;
  nonBinaryPercentage: number;
  femalePercentage: number;
  malePercentage: number;
};

export type GenderDemographicsPercentagePerGender = {
  male: number;
  female: number;
  notSpecified: number;
  nonBinary: number;
};

export type GenderWithPercentage = {
  gender: string;
  percentage: number;
};

export type PreviewPanePodcast = Pick<Podcast, 'title' | 'image' | 'description' | 'author'> &
  Partial<Pick<Podcast, 'itunesCategories'>>;

export type Paginated = {
  currentPage: number;
  length: number;
};

export type PaginatedShows = {
  currentPage: number;
  totalPages: number;
  totalShows: number;
};

export type PaginatedEpisodes = {
  currentPage: number;
  totalPages: number;
  totalEpisodes: number;
  episodes: Episode[];
};

export type CSVType = 'episode' | 'podcast' | 'network';

export type Episode = {
  artworkUrl: string;
  description: string;
  duration: number;
  explict: boolean;
  id: string;
  language: string;
  listeners: number;
  name: string;
  releaseDate: string;
  starts: number;
  streams: number;
  url: string;
};

export type Trailer = {
  id: string;
  name: string;
  artworkUrl: string;
  duration: number;
};

export type LocalePreferenceMap = {
  [name: string]: LocalePreference;
};

export type LocalePreference = {
  s4pLocaleCode: string;
  languageCode: string;
  languageAndRegionCode: string;
  brandVideoUrl: string;
};
